import { matchPath } from "react-router-dom";

/* ============================================================================
   The breadcrumb trail for a route, read off the sidebar's own menu tree.

   A page in the menu gets   Dashboard › <group> › <page>
   (the group is a label, like in the sidebar, not a link). A detail page the
   menu never lists hangs off the listing it was opened from:
   `edit-leave/42` is   Dashboard › Leave › Manage Leave › Edit Leave.

   `sections` is the sidebar's SECTIONS, already filtered by
   navigation/access.js, so a parent this user cannot open simply drops out
   of the trail instead of becoming a link to a page that bounces them.

   The dashboard itself has no trail (an empty array): one crumb pointing at
   the page you are on says nothing.
   ========================================================================== */

// routes the menu does not list, each under the listing it belongs to
const DETAIL = [
  { path: "update-project/:id", parent: "manage-project", label: "Edit Project" },
  { path: "view-project/:id", parent: "manage-project", label: "Project Details" },
  { path: "my-projects/:id", parent: "my-projects", label: "Project Details" },
  { path: "add-tracker/:id", parent: "manage-tracker", label: "Edit Tracker" },
  { path: "add-clickup/:id", parent: "manage-clickup", label: "Edit Tracker" },
  { path: "add-support/:id", parent: "view-support", label: "Edit Support Ticket" },
  { path: "add-anouncements/:id", parent: "annoucement-listing", label: "Edit Announcement" },
  { path: "edit-leave/:id", parent: "manage-leave", label: "Edit Leave" },
  { path: "edit-leave", parent: "manage-leave", label: "Edit Leave" },
  { path: "edit-user/:id", parent: "manage-user", label: "Edit User" },
  { path: "edit-user", parent: "manage-user", label: "Edit User" },
  { path: "view-payslip/:id", parent: "manage-payslip", label: "View Payslip" },
  { path: "edit-payslip/:id", parent: "manage-payslip", label: "Edit Payslip" },
  { path: "duplicate-payslip/:id", parent: "manage-payslip", label: "Duplicate Payslip" },
  { path: "add-todo", parent: "manage-todo", label: "Add To Do" },
  { path: "edit-todo/:id", parent: "manage-todo", label: "Edit To Do" },
  { path: "gallery/:id", parent: "gallery", label: "Folder" },
  { path: "edit-inventory/:id", parent: "inventory-listing", label: "Edit Inventory" },
  { path: "edit-inventory", parent: "inventory-listing", label: "Edit Inventory" },
];

// routed pages with no menu row and nothing above them
const STANDALONE = {
  timeline: "Timeline",
  analysis: "Analysis",
  "pay-slip": "Pay Slips",
  leave: "Leave",
};

const HOME = { key: "home", label: "Dashboard", to: "home" };

// the menu crumbs for a route: [group?, page], or [] when it is not in the menu
const menuCrumbs = (to, sections) => {
  for (const section of sections) {
    for (const item of section.items) {
      if (item.to === to) return [{ key: to, label: item.label, to }];
      const child = item.children?.find((c) => c.to === to);
      if (child) {
        return [
          { key: "group:" + item.key, label: item.label },
          { key: to, label: child.label, to },
        ];
      }
    }
  }
  return [];
};

/** `[{ key, label, to? }]`, Dashboard first and the current page last. */
export const breadcrumbTrail = (pathname, sections) => {
  const path = (pathname ?? "").replace(/^\/+|\/+$/g, "");
  if (!path || path === HOME.to) return [];

  const detail = DETAIL.find((d) => matchPath("/" + d.path, "/" + path));
  let trail;
  if (detail) {
    trail = [
      ...menuCrumbs(detail.parent, sections),
      { key: "detail:" + detail.path, label: detail.label },
    ];
  } else {
    trail = menuCrumbs(path, sections);
    if (!trail.length && STANDALONE[path]) {
      trail = [{ key: path, label: STANDALONE[path] }];
    }
  }
  if (!trail.length) return [];

  const all = [HOME, ...trail];
  // "Manage Projects › Manage Projects": a group named like its page adds nothing
  return all.filter((crumb, i) => crumb.to || all[i + 1]?.label !== crumb.label);
};
